import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Group, Text, Title} from '@mantine/core';
import React, {useMemo} from 'react';

import {getPriorityForComponentType} from 'antibodies/nested-data-creation/spreadsheet-import/AntibodyNestedDataCreationSpreadsheetImportUtil';
import {
  NdcColumnState,
  NdcComponentInputType,
  NdcComponentSequenceType,
  NdcSpreadsheetInterpretation,
  NdcComponentType,
  NDC_EXISTING_ENTITY_COMPONENT_TYPE_LABEL,
  NDC_DNA_BASES_COMPONENT_TYPE_LABEL,
  NDC_AA_SEQUENCE_COMPONENT_TYPE_LABEL,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {assert} from 'Asserts';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import WarningIcon from 'ui-library/icons/WarningIcon';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

export const getLabelForNdcComponentType = (type: NdcComponentType): string => {
  const {componentSequenceType, componentInputType} = type;
  if (componentInputType === NdcComponentInputType.EXISTING_ENTITY_NAME_OR_REGISTRY_ID) {
    return NDC_EXISTING_ENTITY_COMPONENT_TYPE_LABEL;
  } else if (
    componentSequenceType === NdcComponentSequenceType.DNA &&
    componentInputType === NdcComponentInputType.NEW_SEQUENCE
  ) {
    return NDC_DNA_BASES_COMPONENT_TYPE_LABEL;
  } else if (
    componentSequenceType === NdcComponentSequenceType.AA &&
    componentInputType === NdcComponentInputType.NEW_SEQUENCE
  ) {
    return NDC_AA_SEQUENCE_COMPONENT_TYPE_LABEL;
  }
  assert(false, `Invalid component type ${componentSequenceType} ${componentInputType}`);
  return '';
};

export const getColumnOptionDatas = (column: NdcColumnState): Array<OptionData> =>
  column.inferredTypes
    .map((type) => ({
      name: getLabelForNdcComponentType(type),
      value: type,
    }))
    .sort((a, b) => getPriorityForComponentType(a.value) - getPriorityForComponentType(b.value));

const useStyles = createStyles(() => ({
  banner: {
    marginTop: '16px',
  },
  rowHeader: {
    width: '72px',
  },
  sampleValueColumn: {
    width: '255px',
    maxWidth: '255px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface ColumnTypeDropdownProps {
  column: NdcColumnState;
  selectedValue: NdcComponentType;
  optionDatas: Array<OptionData>;
  handleTypeChange: (columnName: string, selectedType: NdcComponentType) => void;
}

function ColumnTypeDropdown({
  column,
  selectedValue,
  optionDatas,
  handleTypeChange,
}: ColumnTypeDropdownProps): JSX.Element {
  return (
    <SelectDropdown
      menu={
        <SearchableMenu
          optionDatas={optionDatas}
          onSelect={(selectedType: NdcComponentType) => {
            handleTypeChange(column.columnName, selectedType);
          }}
          selectedValue={selectedValue}
        />
      }
      displayName={
        column.inferredTypes.length > 1 && column.selectedType == null ? (
          <Group spacing="xs" align="center">
            <WarningIcon />
            {getLabelForNdcComponentType(selectedValue)}
          </Group>
        ) : (
          getLabelForNdcComponentType(selectedValue)
        )
      }
      alwaysDropDown={true}
    />
  );
}

export interface ColumnMappingPaneProps {
  spreadsheetInterpretation: NdcSpreadsheetInterpretation;
  updateSpreadsheetInterpretation: (spreadsheetInterpretation: NdcSpreadsheetInterpretation) => void;
}

export default function ColumnMappingPane({
  spreadsheetInterpretation,
  updateSpreadsheetInterpretation,
}: ColumnMappingPaneProps): JSX.Element {
  const {classes} = useStyles();
  const columns = spreadsheetInterpretation.columns;

  const displayConfirmColumnTypesBanner = useMemo(
    () => columns.some((column) => column.selectedType == null),
    [columns]
  );

  const handleTypeChange = (columnName: string, selectedType: NdcComponentType): void => {
    const updatedSpreadsheetInterpretation: NdcSpreadsheetInterpretation = {
      ...spreadsheetInterpretation,
      columns: spreadsheetInterpretation.columns.map((column) =>
        column.columnName === columnName ? {...column, selectedType} : column
      ),
    };
    updateSpreadsheetInterpretation(updatedSpreadsheetInterpretation);
  };

  const confirmColumnTypes = (): void => {
    const updatedSpreadsheetInterpretation: NdcSpreadsheetInterpretation = {
      ...spreadsheetInterpretation,
      columns: spreadsheetInterpretation.columns.map((column) => {
        assert(column.inferredTypes.length > 0, 'Expected inferredTypes to be non-empty');
        const highestPriorityType = getColumnOptionDatas(column)[0].value;
        return {...column, selectedType: column.selectedType ?? highestPriorityType};
      }),
    };
    updateSpreadsheetInterpretation(updatedSpreadsheetInterpretation);
  };

  return (
    <div>
      <Title order={5} fw={600}>
        Review upload
      </Title>
      <Text size="sm">Here's how we interpreted your spreadsheet headers. Does this look correct?</Text>

      {displayConfirmColumnTypesBanner && (
        <div className={classes.banner}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text="Review each column and confirm that the types are mapped correctly"
            action={{
              actionType: InlineBannerActionType.CUSTOM,
              actionText: 'Confirm column types',
              rightAlign: true,
              actionOnClick: confirmColumnTypes,
            }}
          />
        </div>
      )}

      <table
        className="table"
        {...getTestProps({component: 'ColumnMappingPane', element: 'columnMappingTable'})}
      >
        <thead>
          <tr>
            <th>Column</th>
            <th>Column header</th>
            <th>Sample value</th>
            <th>Column type</th>
          </tr>
        </thead>
        <tbody>
          {columns.map((column, columnIndex) => {
            assert(column.inferredTypes.length > 0, 'Expected inferredTypes to be non-empty');
            const optionDatas = getColumnOptionDatas(column);
            // If there is no selected type, use the first option's value as it is ordered by priority
            const selectedValue: NdcComponentType | undefined = column.selectedType ?? optionDatas[0]?.value;
            return (
              <tr key={column.columnName}>
                <td className={`bulk-import--column-text-column ${classes.rowHeader}`} role="rowheader">
                  <span className="bulk-import--table-text">
                    {SpreadsheetBulkImportUtil.colNumToExcelId(columnIndex)}
                  </span>
                </td>
                <td className="bulk-import--column-text-column">
                  <span className="bulk-import--table-text">{column.columnName}</span>
                </td>
                <td className={`bulk-import--column-text-column ${classes.sampleValueColumn}`}>
                  <span className="bulk-import--table-text">{column.sampleValue}</span>
                </td>
                <td className="bulk-import--column-type-column">
                  <ColumnTypeDropdown
                    column={column}
                    selectedValue={selectedValue!}
                    optionDatas={optionDatas}
                    handleTypeChange={handleTypeChange}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
